import React, { memo, useEffect } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ListItemText,
  Checkbox,
  MenuItem,
  InputLabel,
  OutlinedInput,
  FormControl
} from "@mui/material";
import { getIn } from "formik";
import PropTypes from "prop-types";
import { ErrorMessage, ContexualHelp } from "./../../Notifications";
import { InfoTitle } from "../../InfoTitle";
import { areEqualFieldProps } from "../utils";

import { StyledSelectField } from "./styled";

export const Field = ({
  extractId = option => option.key,
  extractValue = option => option.value,
  extractLabel = item => item.label || item.displayText,
  placeholder,
  multiple = false,
  options = [],
  required,
  label,
  field,
  infoTitle,
  form: { errors, touched },
  isMulti = false,
  shrink,
  contextualHelpText,
  contextualHelpProps = {},
  ErrorMessageComponent = ErrorMessage,
  ...rest
}) => {
  const inputLabel = React.useRef(null);
  const [labelWidth, setLabelWidth] = React.useState(0);
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);
  const MenuProps = {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left"
    },
    PaperProps: {
      style: {
        maxHeight: 300,
        marginTop: "6px",
        zIndex: 9999
      }
    }
  };

  useEffect(() => {
    setLabelWidth(inputLabel.current.textContent);
  }, []);

  const renderValue = selected =>
    !multiple
      ? extractLabel(options.find(item => extractValue(item) === selected) || {})
      : options
          .flatMap(item => (selected.includes(extractValue(item)) ? [extractLabel(item)] : []))
          .join(", ");

  return (
    <FormControl className="formControl" variant="outlined" data-testid="custom-select">
      <InputLabel ref={inputLabel} shrink={shrink}>
        {label}
      </InputLabel>

      <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
        <StyledSelectField
          variantType="basic"
          {...field}
          {...rest}
          renderValue={renderValue}
          multiple={multiple}
          input={<OutlinedInput label={labelWidth} />}
          IconComponent={KeyboardArrowDownIcon}
          error={isError}
          MenuProps={MenuProps}
        >
          {options.map(option => (
            <MenuItem key={extractId(option)} value={extractValue(option)}>
              {multiple ? (
                <>
                  <ListItemText primary={extractLabel(option)} />
                  <Checkbox color="default" checked={field.value.includes(extractValue(option))} />
                </>
              ) : (
                extractLabel(option)
              )}
            </MenuItem>
          ))}
        </StyledSelectField>
      </ContexualHelp>

      {isError && <ErrorMessageComponent error={errorMessage} />}

      {infoTitle && <InfoTitle title={infoTitle} />}
    </FormControl>
  );
};

export const CustomSelect = memo(Field, areEqualFieldProps);
Field.propTypes = {
  extractId: PropTypes.func,
  extractValue: PropTypes.func,
  extractLabel: PropTypes.func,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object),
  required: PropTypes.bool,
  label: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any
  }).isRequired,
  infoTitle: PropTypes.string,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object
  }).isRequired,
  isMulti: PropTypes.bool,
  shrink: PropTypes.bool,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  ErrorMessageComponent: PropTypes.elementType
};
